import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Table2, AlertTriangle, ClipboardCheck,
  FileBadge, GraduationCap, Eye, FileX2, ShieldCheck, FileText, Settings as SettingsIcon, Shield, LogOut
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useHSE } from "../../lib/store";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Executive Dashboard", icon: LayoutDashboard, end: true },
  { to: "/monthly-kpi", label: "Monthly KPI Data", icon: Table2 },
  { to: "/observations", label: "Leading Indicators", icon: Eye },
  { to: "/incidents", label: "Lagging Indicators", icon: AlertTriangle },
  { to: "/inspections", label: "HSSE Inspections", icon: ClipboardCheck },
  { to: "/permit-to-work", label: "Emergency Drills", icon: FileBadge },
  { to: "/audits", label: "Audit & Follow-up", icon: ShieldCheck },
  { to: "/ncr-register", label: "Incident Statistics", icon: FileX2 },
  { to: "/training", label: "Training & Induction", icon: GraduationCap },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Sidebar() {
  const { logout, session } = useAuth();
  const { data } = useHSE();
  const nav = useNavigate();
  const clientName = data.settings?.client || "Marvin HSSE Dashboard";
  const companyName = data.settings?.company || "Personal HSSE Reporting System";
  const systemTitle = data.settings?.systemTitle || "HSSE Command & Reporting System";
  const onLogout = () => {
    if (!confirm("Sign out of the command center?")) return;
    logout();
    toast.success("Signed out securely");
    nav("/login", { replace: true });
  };
  return (
    <aside
      data-testid="app-sidebar"
      className="hidden lg:flex flex-col w-[240px] shrink-0 h-screen sticky top-0"
      style={{ background: "#082B4D" }}
    >
      <div className="px-5 pt-5 pb-4 border-b border-white/8">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "#18B84F" }}>
            <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="text-white font-heading font-bold text-[15px] tracking-tight truncate max-w-[155px]" title={systemTitle}>{systemTitle}</div>
          </div>
        </div>
        <div className="mt-4 rounded-lg bg-white/6 border border-white/10 px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/55 font-semibold">Client</div>
          <div className="text-white text-sm font-semibold mt-0.5 truncate">{clientName}</div>
          <div className="text-[10px] text-white/55 truncate">{companyName}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2.5">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`sidebar-${n.label.toLowerCase().replace(/[^\w]+/g, "-")}-link`}
            className={({ isActive }) =>
              `group relative flex items-center gap-2.5 px-3 py-2 rounded-lg mb-0.5 transition-all duration-150 ${
                isActive
                  ? "text-white shadow-sm"
                  : "text-white/65 hover:bg-white/6 hover:text-white"
              }`
            }
            style={({ isActive }) => isActive ? { background: "#18B84F" } : {}}
          >
            <n.icon className="w-[17px] h-[17px] shrink-0" strokeWidth={1.75} />
            <span className="text-[13px] font-medium">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-2.5 pb-3 pt-2 border-t border-white/8">
        <button
          data-testid="sidebar-logout-btn"
          onClick={onLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-rose-500/15 transition"
        >
          <LogOut className="w-[17px] h-[17px] shrink-0" strokeWidth={1.75} />
          <span className="text-[13px] font-medium">Sign out</span>
          {session?.user?.username && (
            <span className="ml-auto text-[10px] text-white/40 font-mono truncate max-w-[80px]">@{session.user.username}</span>
          )}
        </button>
      </div>

      <div className="px-4 py-3 border-t border-white/8 text-[10px] text-white/40 font-mono">
        v2.0 | {new Date().getFullYear()} Marvin HSSE
      </div>
    </aside>
  );
}
